import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { ArrowLeft, AlertTriangle, CheckCircle, Clock, XCircle, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DeviationsPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [deviations, setDeviations] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (selectedProject) {
      fetchData(selectedProject);
    }
  }, [selectedProject]);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
      if (response.data.length > 0) {
        setSelectedProject(response.data[0].project_id);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchData = async (projectId) => {
    setLoading(true);
    try {
      const [devRes, milRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}/deviations`),
        axios.get(`${API}/projects/${projectId}/milestones`)
      ]);
      setDeviations(devRes.data);
      setMilestones(milRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSeverityConfig = (severity) => {
    switch (severity) {
      case 'high':
        return { bg: 'bg-red-50', border: 'border-red-500', text: 'text-red-700', badge: 'bg-red-100 text-red-700' };
      case 'medium':
        return { bg: 'bg-amber-50', border: 'border-amber-500', text: 'text-amber-700', badge: 'bg-amber-100 text-amber-700' };
      case 'low':
        return { bg: 'bg-green-50', border: 'border-green-500', text: 'text-green-700', badge: 'bg-green-100 text-green-700' };
      default:
        return { bg: 'bg-gray-50', border: 'border-gray-500', text: 'text-gray-700', badge: 'bg-gray-100 text-gray-700' };
    }
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'completed':
        return { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-100' };
      case 'in_progress':
        return { icon: Clock, color: 'text-blue-600', bg: 'bg-blue-100' };
      case 'on_track':
        return { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-100' };
      case 'delayed':
        return { icon: XCircle, color: 'text-red-600', bg: 'bg-red-100' };
      case 'upcoming':
        return { icon: Clock, color: 'text-gray-400', bg: 'bg-gray-100' };
      default:
        return { icon: Clock, color: 'text-gray-400', bg: 'bg-gray-100' };
    }
  };

  const statusLabel = (status) => {
    const map = {
      completed: t('deviations.completed'),
      in_progress: t('deviations.inProgress'),
      on_track: t('deviations.onTrack'),
      delayed: t('deviations.delayed'),
      upcoming: t('deviations.upcoming')
    };
    return map[status] || status.replace('_', ' ');
  };

  const severityLabel = (sev) => {
    const map = { high: t('deviations.high'), medium: t('deviations.medium'), low: t('deviations.low') };
    return (map[sev] || sev).toLocaleUpperCase();
  };

  if (loading && deviations.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="w-12 h-12 border-3 border-gray-200 border-t-red-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  const highCount = deviations.filter(d => d.severity === 'high').length;
  const mediumCount = deviations.filter(d => d.severity === 'medium').length;
  const lowCount = deviations.filter(d => d.severity === 'low').length;

  return (
    <div className="pb-20 bg-gray-50 min-h-screen" data-testid="deviations-page">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-4 sticky top-0 z-10">
        <div className="flex items-center gap-3 mb-3">
          <button onClick={() => navigate('/')} className="p-1">
            <ArrowLeft size={20} className="text-gray-600" />
          </button>
          <div>
            <h1 className="text-lg font-bold text-gray-900">{t('deviations.title')}</h1>
            <p className="text-xs text-gray-500">{t('deviations.subtitle')}</p>
          </div>
        </div>
        
        <Select value={selectedProject} onValueChange={setSelectedProject}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder={t('common.selectProject')} />
          </SelectTrigger>
          <SelectContent>
            {projects.map((project) => (
              <SelectItem key={project.project_id} value={project.project_id}>
                {project.project_code} - {project.project_name.substring(0, 25)}...
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="px-4 py-4 space-y-4">
        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-3">
          <Card className="border-l-4 border-red-500">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-red-600">{highCount}</p>
              <p className="text-xs text-gray-500">{t('deviations.high')}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-amber-500">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-amber-600">{mediumCount}</p>
              <p className="text-xs text-gray-500">{t('deviations.medium')}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-green-500">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-green-600">{lowCount}</p>
              <p className="text-xs text-gray-500">{t('deviations.low')}</p>
            </CardContent>
          </Card>
        </div>

        {/* Deviations List */}
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-gray-700">{t('deviations.activeDeviations')}</h3>
          
          {deviations.map((deviation, index) => {
            const config = getSeverityConfig(deviation.severity);
            const statusConfig = getStatusConfig(deviation.status);
            const StatusIcon = statusConfig.icon;
            
            return (
              <Card key={index} className={`border-l-4 ${config.border} ${config.bg}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <AlertTriangle size={16} className={config.text} />
                      <span className="font-semibold text-sm text-gray-900">{deviation.area}</span>
                    </div>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${config.badge}`}>
                      {severityLabel(deviation.severity)}
                    </span>
                  </div>
                  
                  <p className="text-sm text-gray-700 mb-3">{deviation.description}</p>
                  
                  <div className="bg-white/60 rounded p-2 mb-3">
                    <p className="text-xs text-gray-500 mb-1">{t('deviations.impact')}</p>
                    <p className="text-sm font-medium text-gray-800">{deviation.impact}</p>
                  </div>
                  
                  <div className="bg-white/60 rounded p-2 mb-3">
                    <p className="text-xs text-gray-500 mb-1">{t('deviations.actionPlan')}</p>
                    <p className="text-sm text-gray-700">{deviation.action}</p>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className={`flex items-center gap-1 px-2 py-1 rounded ${statusConfig.bg}`}>
                      <StatusIcon size={12} className={statusConfig.color} />
                      <span className={`text-xs font-medium ${statusConfig.color}`}>
                        {statusLabel(deviation.status)}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Milestones Timeline */}
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-gray-700">{t('deviations.projectMilestones')}</h3>
          
          <Card>
            <CardContent className="p-4">
              <div className="relative">
                {milestones.map((milestone, index) => {
                  const statusConfig = getStatusConfig(milestone.status);
                  const StatusIcon = statusConfig.icon;
                  const isLast = index === milestones.length - 1;
                  
                  return (
                    <div key={index} className="flex gap-3 pb-4">
                      {/* Timeline connector */}
                      <div className="flex flex-col items-center">
                        <div className={`w-8 h-8 rounded-full ${statusConfig.bg} flex items-center justify-center`}>
                          <StatusIcon size={16} className={statusConfig.color} />
                        </div>
                        {!isLast && (
                          <div className="w-0.5 flex-1 bg-gray-200 my-1" />
                        )}
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 pb-2">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className={`font-medium text-sm ${milestone.status === 'completed' ? 'text-gray-500' : 'text-gray-900'}`}>
                              {milestone.name}
                            </p>
                            <p className="text-xs text-gray-400 mt-0.5">
                              {t('common.planned')}: {new Date(milestone.planned).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </p>
                            {milestone.actual && (
                              <p className="text-xs text-green-600 mt-0.5">
                                {t('common.actual')}: {new Date(milestone.actual).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                              </p>
                            )}
                          </div>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusConfig.bg} ${statusConfig.color}`}>
                            {statusLabel(milestone.status)}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default DeviationsPage;
